import * as React from 'react';
import { Nav, Overlay, Popover, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './settings-announcement.css';

// Bump this id whenever a NEW announcement should re-surface for everyone who
// has already dismissed the previous one. The "New" badge reappears — and the
// popover opens itself on the next page load — for any user whose localStorage
// doesn't yet have this id marked as seen.
// Bumped when the August batch gained the previous-collection sections, the
// practitioner's own report sections and the menu reorganization: anyone who
// dismissed the first August note has not seen those.
// Bumped again for the release itself: the August note is rewritten around
// markers from any test, and this is the batch practitioners actually see, so
// it starts unseen for every account regardless of what any earlier id left
// behind in their browser.
// Bumped for the September 26 release, which gets its OWN block rather than
// joining the month one above it: two releases went out in the same month, so
// the heading carries the day. It holds the comparison Notes column and its
// range columns, the per-section Insights Panel PDF choice, Systemic Formulas
// dosing, the practitioner's own section order, the new lab formats and the
// client-details icons.
// An October block for My Ranges was written here on 2026-09-30 and taken out
// again on 2026-10-01, with the id reverted: the feature went back behind
// myRangesVisible() before any of it was deployed, and a bullet naming a page
// prod does not carry is the failure the September block below already guards
// against. Nothing shipped under 'whats-new-2026-10', so reverting the id
// cannot un-dismiss a note anyone has already seen.
//
// IT IS ANNOUNCED NOW, in the October 13 block, rewritten for what actually
// ships that day: ferritin alone rather than the five markers the 2026-09-30
// draft listed, plus the Sources panel, which did not exist when that draft
// was written. The block was dated October 11 and went to prod under
// 'whats-new-2026-10-11' on the 11th and the 12th; it is re-dated and the id
// bumped with it so the My Ranges bullets reach accounts that have already
// marked the October 11 note seen. Three bullets in it are therefore shown a
// second time, which is the cost of adding to a block rather than starting
// one, and is the placement the user asked for.
// Bumped on 2026-10-05 for the October 5 block below, which carries only what
// prod will have on the day it lands: the two new report types, the My Markers
// library search and its carry-forward note, and the .txt raw data export. My
// Ranges and leptin stay out, each for the reason recorded in that block.
// Nothing has ever shipped under an id beginning 'whats-new-2026-10', so this is
// still the first October note any practitioner will be shown.
// Bumped again on 2026-10-07 for the tab rename. This one HAS to re-surface
// rather than ride along quietly: the October 5 note went to prod on 2026-10-06,
// so every practitioner has already dismissed that id, and the first tab of
// every section is about to be called something else. A practitioner who opens
// a report looking for "Reference Range" and does not find it has to be told
// where it went, by the note opening itself, not by support. The same is now
// true of the "Vitamin & Mineral Status" section, renamed on 2026-10-08 and
// announced in the same block.
// Moved to -08 on 2026-10-08 with that block's date, which slipped past its own
// day while prod stayed on the October 5 note. -07 DID ship, but only to slava,
// in 0e37690 earlier that day: no practitioner has seen it, so re-pointing the
// id cannot un-dismiss anyone's note, and anyone who opened slava after that
// deploy has already dismissed -07 and needs -08 to be shown the section
// rename at all. Keeping id and date in step is what the blocks below do.
// Bumped on 2026-10-10 for the October 10 block below. -08 DID reach prod, in
// 66ed0e1 on 2026-10-08, so unlike the -07/-08 move above this is not a
// re-point of an unshipped id: every practitioner has -08 marked seen, and a
// bullet appended to that block is a bullet nobody is shown.
// It earns the re-surface on the chart bullet alone. Every bar graph in all 18
// sections and in every client PDF is about to look different, and the only
// place that says no result was re-graded is this note.
// Moved to -11 on 2026-10-11 with that block's date, because the release slipped
// a day. -10 NEVER DEPLOYED: the committed id at HEAD and at the last prod
// deploy (66ed0e1) is still -08, so -10 lived only in the working tree and
// nobody on either environment has it marked seen. Re-pointing it therefore
// cannot un-dismiss anyone's note, and -11 is the first note after -08 for
// every account — the same situation as the reverted October My Ranges id above.
// The prebuilt-lab-panels bullet came OUT of the block in the same pass: that
// release is not going to prod, labPanelsVisible() is visibleExceptProd(), and
// the bullet's own note said to move it if the gate stayed shut. It is back in
// the waiting list with its approved wording.
//
// BUMPED TO -13b ON 2026-10-13, THE SAME DAY -13 REACHED PROD (5e8b824). The
// gate came off the prebuilt lab panels hours later, so that bullet finally has
// a release, and it went INTO the existing October 13 block at the user's
// instruction rather than into a second block dated the same day: there were
// two prod releases on 2026-10-13 and one date a practitioner reads.
//
// WHICH IS WHY THIS BUMP IS NOT OPTIONAL. The -13 block is live and every
// practitioner who opened it has -13 marked seen, so a bullet appended to it
// without a new id is a bullet nobody is ever shown. This is the trap the -08
// note above documents, met twice in one day. The suffix re-surfaces the whole
// October 13 note, which re-shows four bullets people have already read; that
// is the accepted cost of keeping one block per date.
//
// Re-date the block, and this id with it, if the deploy slips past today.
const ANNOUNCEMENT_ID = 'whats-new-2026-10-13b';

const ANNOUNCEMENT_TITLE = "What's New";
const ANNOUNCEMENT_INTRO = "We've been busy — here's what's new in LabSmarts:";

// A feature bullet. The object form leads with `name` in bold so the thing being
// announced is readable at a glance instead of buried mid-sentence — a list of
// full sentences all in one weight is what practitioners were skimming past.
// `desc` is optional: a bullet that doesn't split into name-plus-explanation is
// just a bold name.
//
// The plain-string form is what the earlier releases use and renders unchanged,
// so re-styling a new release never rewrites the ones already published.
//
// `link` is for a bullet whose answer lives somewhere else: the bibliography
// section for a range that moved, a help page, a lab's own documentation. It
// renders after `desc` and opens in a new tab, because this note sits over the
// app and a practitioner following a citation has not finished reading it.
// Per-bullet rather than per-release: `blogUrl` below is the whole release's
// write-up, and a release with four unrelated bullets has no single one.
//
// URLs here are written out rather than derived (bibliographyUrlFor() builds
// the same `#ferritin` anchor from BIBLIOGRAPHY_ANCHORS). A changelog entry is
// FROZEN TEXT: it should keep pointing where it pointed on its release date
// even if the app's own link later moves, and this file deliberately imports
// nothing from the models.
type FeatureItem =
  | string
  | {
      name: string;
      desc?: string;
      group?: string;
      link?: { href: string; text: string };
    };

/**
 * Split a release's features into their `group` runs, in list order.
 *
 * A dozen bullets in one column is a wall a practitioner skims past, and the
 * ones they care about are usually all in one family — everything about their
 * own markers, or everything that changed on the report. Grouping is opt-in per
 * feature: releases whose features carry no group (every one before August
 * 2026) come back as a single unlabeled run and render exactly as they did.
 */
export function groupFeatures(
  features: FeatureItem[],
): { group?: string; items: FeatureItem[] }[] {
  const runs: { group?: string; items: FeatureItem[] }[] = [];
  (features || []).forEach(feature => {
    const group = typeof feature === 'string' ? undefined : feature.group;
    const last = runs[runs.length - 1];
    if (last && last.group === group) {
      last.items.push(feature);
    } else {
      runs.push({ group, items: [feature] });
    }
  });
  return runs;
}

// Running changelog, newest release first. Each release groups its features and
// setting groups under a release date. To announce a new batch, add a new entry
// at the TOP (keep the older ones below) and bump ANNOUNCEMENT_ID so the "New"
// badge re-surfaces and the note opens itself. `settings` names match the
// headers on the Report Settings page and the description explains what each controls.
const RELEASES: Array<{
  date: string;
  features: FeatureItem[];
  settings: Array<{ name: string; desc: string }>;
  // Optional "Read more" write-up for this specific release.
  blogUrl?: string;
}> = [
  /*
   * WAITING FOR A BLOCK. Everything here is built and has no bullet yet, and
   * none of it can have one: it is held off prod, and a bullet naming a button
   * nobody on the live site has is worse than silence.
   *
   *   - VITAMIN D on My Ranges, authored 2026-10-23 and held off prod
   *     2026-10-26 at the user's instruction while the ranges themselves are
   *     still under review. Gated by EDITABLE_RANGE_ATTRIBUTES_HELD_OFF_PROD
   *     in RangeCatalog.ts, so it is live on slava and invisible in prod.
   *     A bullet was written for it and REMOVED with that decision, along with
   *     the ANNOUNCEMENT_ID bump to -23 that would have carried it: bumping
   *     the id re-opens this modal for everyone, and re-opening it to announce
   *     nothing is worse than not bumping it. Whatever eventually announces
   *     this must describe the ranges that actually ship, which may not be
   *     30/50/80/100.
   *   - MVX Plus, behind mvxVisible(), and now the analysis PDF section too.
   *     The 7-Step Blueprint is held back separately and indefinitely
   *     (mvxBlueprintVisible() is a flat false), so whatever eventually
   *     announces MVX must not describe it.
   *   - Iron Insights, behind visibleExceptProd() and still listed in
   *     SECTIONS_PENDING_CLINICAL_REVIEW. Its gauge was rescored on
   *     2026-10-09 (the iron quartet now counts as two readings rather than
   *     four markers, because % saturation is derived from the other two), so
   *     a bullet written before that date would describe the old needle.
   *   - The Function Health APP export, which unlike the four above IS live in
   *     prod: it shipped with uploadPdf on 2026-10-08, unannounced, and its
   *     bullet is blocked on two fixes rather than on a gate. Lipoprotein (a)
   *     charts in the wrong unit (Function prints nmol/L, the lipoproteinA
   *     range rows are a mg/dL scale, and Labcorp and the Quest-sourced
   *     Function reports feed the same field the same way, so the fix belongs
   *     in the ranges), and the only sample on disk stops mid-urinalysis. Do
   *     not confuse it with the Function Health reports that have read for
   *     months: those are Quest-performed and go through Quest's rules.
   *
   * PREBUILT LAB PANELS LEFT THIS LIST FOR GOOD ON 2026-10-13, and the round
   * trip is the lesson: out on 2026-10-10, back on 2026-10-11 when the release
   * was held off prod, out again now that `labPanelsVisibility.ts` is deleted
   * and the button is live. The rule that made each move correct was the
   * bullet's own: it names the button by the words printed on it, so the two
   * travel together and neither ships without the other. The count it told the
   * next writer to re-check had moved again (nineteen to twenty-two), which is
   * why that instruction was in there.
   *
   * "Where this range comes from" on My Markers left this list on 2026-10-10
   * and is announced in the block below. It had been parked rather than
   * blocked, waiting for a batch worth bumping ANNOUNCEMENT_ID for, and the
   * chart-band change is that batch.
   */
  {
    /*
     * ITS OWN BLOCK. The October 8 block below reached PROD, in 66ed0e1 on
     * 2026-10-08, which is the case its own note told the next writer to check
     * for: once that block is live its id is marked seen in every browser, so a
     * bullet appended to it is a bullet nobody is shown.
     *
     * THE CHART BULLET NOW SITS LAST, and it has lost two arguments on its way
     * down. It led this block when it was written; the Lab Uploads bullet went
     * above it on 2026-10-10, and My Markers went above it on 2026-10-13, both
     * at the user's instruction and both for the same reason: they are things a
     * practitioner could not do at all the day before, and the chart bullet is
     * a reassurance about something they will see whether or not they read the
     * note.
     *
     * IT IS STILL WORTH CARRYING, which is the only claim it has left. Every
     * bar graph in all 18 sections and in every client PDF now looks different.
     * Nobody has to be sold on that, but the question it raises, whether the
     * ranges moved or anything re-graded, has exactly one place to be answered,
     * and this is it. Last in the list is the right place for an answer to a
     * question rather than an announcement.
     *
     * FIVE BULLETS NOW. It was three until 2026-10-13, when the prebuilt lab
     * panels went to prod and their bullet joined as the first of the two under
     * "My Markers" — see its own note for why it leads its group and why this
     * block took it rather than a second October 13 block. The My Markers group
     * moved ahead of Report charts in the same pass.
     *
     * THREE MORE WERE WRITTEN FOR THIS BLOCK on
     * 2026-10-10 and taken out again the same day, at the user's instruction.
     * All of them SHIPPED, so this is a record of deployed work left
     * unannounced rather than of anything still gated:
     *
     *   - THE IMMUNE PATTERN RENAME. Eight labels change (RootCause.ts, and
     *     the matching FurtherConsiderations rows): "Suppressed" and "Acute
     *     Immune Activity" become "Reduced" and "Increased", and the
     *     innate/adaptive axis moves from "Response" to "Dominance". Worth
     *     knowing, if this ever needs writing: a rename is the kind of change
     *     that is MISSED rather than noticed, and reports already delivered to
     *     clients carry the old wording, so a practitioner who re-exports one
     *     will find the names disagree with the PDF in their client's inbox.
     *     The two rules a bullet would have to carry are that the magnitude
     *     word belongs to "Immune Activity" and comes from the WBC, and the arm
     *     word belongs to "Dominance" and comes from the NLR.
     *   - CHI HEALTH chart prints. A bullet was written here and withdrawn; the
     *     2026-10-08 call in the block below therefore stands unchanged, and
     *     its reasoning is still the live one. If it is ever revisited, that
     *     note says what the bullet has to carry.
     *   - MYCHART'S OWN NAMES, headed "Reports from a patient portal read
     *     too". The aliases are in utils.js and those rows fill their markers
     *     whether or not anyone is told. What a bullet would have to carry, if
     *     it is ever wanted: a patient portal prints display names no lab
     *     template uses, so "Auto WBC" is the white count, "LDL Calculated" is
     *     the LDL and "Neutrophils Relative Percent" is the differential.
     *     Withdrawn because the upload bullet above is the news and this is a
     *     detail inside it.
     *
     * RE-DATE IT, AND THE ID WITH IT, IF THE DEPLOY SLIPS. The two blocks below
     * were each re-dated for exactly that, and the date a practitioner reads
     * has to be the day the note reached them.
     */
    date: 'October 13, 2026',
    features: [
      {
        /*
         * THE LEAD, at the user's instruction on 2026-10-10, ahead of the chart
         * bullet that had been written to lead this block. It is the only
         * bullet here that gives a practitioner something they could not do at
         * all yesterday: a client who sends a photograph of their labs has been
         * an afternoon of typing since the day this app shipped.
         *
         * GROUPED AS "Lab Uploads", NOT "PDF Lab Uploads" as every upload
         * bullet before it. The heading sits directly over a sentence whose
         * whole point is that the file no longer has to be a PDF, and a
         * practitioner scanning headings for their problem is holding a
         * photograph.
         *
         * THE NAME IS THE UMBRELLA AND THE SHAPES OPEN THE SENTENCE. A
         * practitioner matches this bullet to the thing in their hand, so the
         * shapes have to appear somewhere: "fax" and "screenshot" are how a
         * report arrives, and neither reader thinks of their file as "an
         * image". They sit in the first clause rather than the heading, which
         * would need five cases in it. The link in the app is quoted word for
         * word, so the bullet and the control agree on screen.
         *
         * BOTH REASONS, NOT JUST THE PICTURE. The same prompt is the way in
         * for a report from a lab LabSmarts has no parser for, and that file
         * is a perfectly ordinary PDF. A bullet that only talked about
         * pictures would leave the practitioner holding one with no idea this
         * is theirs to use.
         *
         * NO LIST OF ASSISTANTS, which is a cut, not an oversight. Naming four
         * and stopping promises a restriction that does not exist, and "any AI
         * assistant" says the true thing in three words. The four names are
         * still on the screen the link opens, where a practitioner who does
         * not have one yet is actually choosing.
         *
         * THE LAST CLAUSE EARNS THE TRUST AND IS NOT OPTIONAL. The reasonable
         * fear about handing a lab report to an AI is a confidently wrong
         * number, and the answer is the `??` rule: a character the assistant
         * could not read holds the whole row off the form and names it back. A
         * bullet that sold the convenience and left that out would be selling
         * the part a careful practitioner distrusts.
         *
         * SHORTENED on 2026-10-12, at the user's instruction. It had grown to
         * 113 words against a 25-word house average, and twice as long as
         * anything else on the page reads as the release note nobody finishes.
         * What went: the explanation of WHY a picture has no text, which the
         * practitioner does not need and the modal gives anyway; the four
         * assistant names; and the line about several photos of one report,
         * which is on that screen too. What stayed: both reasons, the quoted
         * control, the three steps, and the `??` rule.
         */
        group: 'Lab Uploads',
        name: 'Upload a report LabSmarts cannot read yet',
        desc:
          'for a photo, scan, screenshot, fax, or a lab LabSmarts does not support yet: click "Photo, scan, or unsupported lab?" beside Browse, give the prompt and the report to any AI assistant, and upload the .csv it returns. Anything the assistant could not read is left off and named on screen, never guessed',
      },
      {
        /*
         * MY RANGES, below the upload bullet at the user's instruction
         * (2026-10-13), not above it: the upload prompt is still the lead of
         * this block and the thing most practitioners came for.
         *
         * FIVE BULLETS CUT TO THREE, at the user's instruction, the same day
         * and for the same reason the upload bullet above was shortened. 146
         * words against a 25-word house median, in a block where Lab Uploads,
         * Report charts and My Markers get one bullet each, made this read as
         * the release note nobody finishes.
         *
         * WHAT WENT. "A way back on every row" (Reset sits on each population):
         * a practitioner meets that control the moment they need it, on the row
         * they are standing on, so a release note spends words teaching
         * something the page hands over for free. "Marked for you, not for your
         * client" was not dropped but FOLDED into the retroactivity bullet,
         * which was already the one about what reports do and say. Also gone:
         * "including children and each trimester", which is detail the page's
         * own intro carries at the moment it matters.
         *
         * WHAT STAYED, and why these three. The feature, the evidence, and the
         * consequence. The third is the only one with a cost attached, so it is
         * the one a practitioner must not learn by discovering it on a report
         * they already sent.
         *
         * FERRITIN IS NAMED. The bullets written on 2026-09-30 listed five
         * markers; EDITABLE_RANGE_ATTRIBUTES went to ferritin alone on
         * 2026-10-12, because only ferritin has its bounds sourced in
         * RangeSources.ts. Naming it keeps the heading true in what is a dated
         * record: this block will still read October 13 once the list has five
         * markers in it.
         *
         * "FOR FERRITIN", NOT "STARTING WITH FERRITIN", which was tried and
         * rejected the same day (the user's call, and she was right). The worry
         * it was meant to answer is real but second order: "for" frames this as
         * a ferritin feature when it is a capability, and a practitioner whose
         * interest is thyroid may skip the bullet, which costs the feedback
         * this early release exists to collect. "Starting with" costs more. It
         * reads as sequencing the practitioner's OWN work among markers they
         * can already edit, so it promises a choice the page does not offer,
         * and the practitioner who acts on it opens the page looking for the
         * others. A heading that disappoints on arrival is worse than one that
         * is skipped. The roadmap job moves to the desc's last sentence, which
         * says more are coming AND who decides the order, with no ambiguity
         * about what is editable today.
         *
         * NO BULLET FOR THE ENTITLEMENT. customRangesAllowed is true for every
         * account and nothing in the UI mentions it, so naming it here would
         * introduce a restriction practitioners do not have.
         */
        group: 'My Ranges',
        name: 'Set your own reference ranges for ferritin',
        desc:
          'replace the lab, optimal, and alarm numbers LabSmarts grades against, separately for each client population. Which markers come next depends on what practitioners ask for',
      },
      {
        group: 'My Ranges',
        name: 'Every number says where it came from',
        desc:
          'Sources on the marker opens the studies behind each bound, quoting the figure from the paper, with a PubMed link to read it in full',
      },
      {
        group: 'My Ranges',
        name: 'A range you change applies to every report',
        desc:
          'including tests already analyzed, so a correction reaches the clients you have already seen. The save dialog says so first, and a client’s report never says a range was changed',
      },
      {
        group: 'My Markers',
        /*
         * FIRST UNDER "My Markers", at the user's instruction on 2026-10-13,
         * and in THIS block rather than a second one dated the same day: there
         * were two prod releases on 2026-10-13 and one date the practitioner
         * reads, so one block carries both.
         *
         * THAT MAKES THE ID BUMP LOAD-BEARING. This block reached prod earlier
         * the same day in 5e8b824, so every practitioner who opened it has
         * -13 marked seen and would never be shown a bullet added to it.
         * ANNOUNCEMENT_ID is -13b for exactly that, which re-surfaces the whole
         * note: the four bullets below are shown a second time, and that is the
         * accepted cost of keeping one October 13 block.
         *
         * IT LEADS ITS GROUP because it is the only bullet in either release
         * that gives a practitioner a thing they could not do at all before,
         * and the bullet under it reports a box moving on a form.
         *
         * THE WORDING IS THE ONE APPROVED ON 2026-10-10, carried in the waiting
         * list above since it was pulled back out on 2026-10-11, SHORTENED on
         * 2026-10-13 at the user's instruction and corrected twice:
         *
         *   1. THE COUNT, twice, and the second correction is the one worth
         *      reading. The first was arithmetic: the old note told the next
         *      writer to re-check the number and was right to, because
         *      nineteen became twenty-two and then twenty-nine inside one day.
         *
         *      THE SECOND WAS THAT THE NUMBER WAS THE WRONG NUMBER. "Twenty-
         *      nine tests" counts CONTAINERS, and only sixteen of them are a
         *      panel; the other thirteen are a single marker (five Cardio IQ
         *      add-ons, the two lipid rows, UIBC, sTfR, MMA, lead, mercury,
         *      rheumatoid factor). So the figure a practitioner was given
         *      described neither what they get nor how many things it is.
         *
         *      THE PAYLOAD IS 121 MARKERS, and that is what the bullet leads
         *      with now, because the question behind it is "how much typing
         *      does this save me". The 16-and-13 split follows, since it is
         *      also the reason the button is called panel/marker rather than
         *      panel. The per-lab split went to make room: which lab is
         *      covered matters, how the twenty-nine divide between them does
         *      not.
         *
         *      RE-COUNT ALL THREE before the next deploy. They have moved on
         *      every single release this feature has had.
         *   2. "panel" BECAME "test", AND THE BUTTON NAME GAINED "/marker",
         *      matching the rename the same day. The bullet names the button by
         *      the words printed on it; that rule is what kept the two together
         *      through three releases, so a rename travels into the bullet or
         *      the rule is broken.
         *
         * WHAT THE SHORTENING CUT, so it is not re-added by someone reading the
         * waiting list's longer draft: the clause about where the markers are
         * filed ("under a section you name, or straight into the report section
         * it belongs in"), and the list of example tests. The destination is on
         * screen at the moment it matters, stated in the dialog before anything
         * is added, and the examples are what the search box is for. What
         * stayed is the one sentence a practitioner cannot work out for
         * themselves: the button exists, and it fills the ranges too.
         *
         * THE THREE ENTRIES THAT FILE INTO A REPORT SECTION ARE NOT NAMED.
         * Nobody shops for "VLDL Cholesterol Cal"; they open the button and
         * search their lab.
         */
        name: 'Add a whole lab panel in one step',
        desc:
          'the new "+ Add a lab panel/marker" button on My Markers takes every marker in a panel into your library at once, with the lab’s own names, units, and reference ranges. 121 markers to start, across sixteen panels and thirteen single tests from Labcorp and Quest Diagnostics',
      },
      {
        group: 'My Markers',
        /*
         * PARKED SINCE 2026-10-08 and drafted in the waiting list above, which
         * is where the wording comes from. It rides this block rather than
         * earning one of its own, exactly as that note said it would.
         *
         * THE LAST SENTENCE IS THE HALF THAT EARNS THE BULLET: a paragraph
         * people have been reading for months is gone from the form, and a
         * change nobody is told about is noticed only as a loss.
         *
         * SHORTENED on 2026-10-10, at the user's instruction, with the chart
         * bullet above. Two things went: "It is for your own reference, and",
         * which was the same promise the clause after it makes better, and the
         * closing "where it is one click away instead of under every marker you
         * open", which argued for the move rather than reporting it. Where the
         * guidance went is the news; that it is an improvement is the reader's
         * call.
         */
        name: 'Record where a marker’s range came from',
        desc:
          'an optional line on any marker you have added yourself, with a box for a PubMed ID or DOI that becomes a link. It never appears on a client’s report. The guidance on filling in the six range boxes has moved to the information icon beside Reference Range',
      },
      {
        group: 'Report charts',
        /*
         * WRITTEN AS "NOTHING CHANGED", which is the unusual shape here: the
         * visible half of this bullet needs no explaining, and the half worth
         * the line is everything that did NOT move. Every status, pill, count,
         * tally and table tint still reads the hard optimal boundary; the fade
         * is fill only.
         *
         * SHORTENED on 2026-10-10, at the user's instruction. Three things
         * went: "on screen and in the PDF", which "every bar graph" already
         * covers; the clause listing the status, pill and count as unchanged,
         * since "nothing was re-graded" is that sentence said once; and the
         * closing clause on the lab's own printed limits staying a crisp edge,
         * which answered a question only a reader looking for it would ask.
         * What is left is the change, then the reassurance.
         *
         * NOT CLAIMED: that this is new information, or that anything reads
         * differently. It does not and nothing does.
         */
        name: 'The lab range bands now ease into the optimal range',
        desc:
          'on every bar graph, the yellow band leans toward green where it meets the optimal range instead of stepping straight from one color to the other. Nothing was re-graded: the boundary has not moved, and a result that read optimal still reads optimal',
      },
    ],
    // Nothing on the Report Settings page in this release.
    settings: [],
  },
  {
    /*
     * ITS OWN BLOCK, not a bullet added to October 5 below. That block shipped
     * to prod on 2026-10-06 and its id is already marked seen in every
     * practitioner's browser, so a bullet appended to it is a bullet nobody is
     * shown (the same trap the September 26 block documents).
     *
     * TWO RENAMES FIRST, AND THEY KEEP THE TOP OF THE NOTE. A rename of the tab
     * that opens first in all 18 sections, and a rename of one of those
     * sections, are not small changes to the person who has been clicking them
     * for two years, and they are the only kind of change that is MISSED rather
     * than noticed: nothing new appears, a familiar word is just gone. A reader
     * who stops after the first group has read the part they cannot work out
     * for themselves. (Written as one bullet on 2026-10-07; the section rename
     * joined it on 2026-10-08, while this block was still unshipped. See that
     * bullet's own note.)
     *
     * THE THIRD BULLET IS HERE BECAUSE THE LAMBDA WENT FIRST. uploadPdf shipped
     * to prod on 2026-10-08, ahead of this frontend, which means the Quest half
     * of the My Markers autofill is ALREADY working on the live site. The
     * announcement is the only part still missing, and this block is the only
     * vehicle for it: bumping ANNOUNCEMENT_ID again later to carry one bullet
     * spends every practitioner's attention a second time. It sits under its
     * own group heading, after the renames, for the reason above.
     *
     * RE-DATED from October 7 on 2026-10-08, per the rule in the block below:
     * the deploy slipped, prod is still carrying the October 5 note, and the
     * date a practitioner reads has to be the day the note reached them. Move
     * it again, and the id with it, if it slips further.
     */
    date: 'October 8, 2026',
    features: [
      // WHAT IS DELIBERATELY NOT HERE: the prebuilt lab panels ("+ Add a lab
      // panel/marker" on My Markers). They were built ungated and were found
      // that way in the audit for this deploy; labPanelsVisible() held them off
      // prod from 2026-10-07, so a bullet here would have named a button nobody
      // on the live site had. That gate opened on 2026-10-13 and they are
      // announced in the October 13 block above, which is "whatever release
      // opens that gate" as this note asked. MVX stays out for the reason the
      // October 5 block below records; My Ranges is in that same block.
      //
      // ALSO NOT HERE, AND THIS ONE DID SHIP: CHI Health reports. The parser
      // (parsers/chiHealth.js, added 2026-10-07) reads a results print from a
      // CHI Health chart, which is Epic's Results Review screen rather than a
      // lab's own template, and it went to prod with uploadPdf on 2026-10-08.
      // So this is a record of a deployed feature that was deliberately left
      // unannounced, not of something still gated: the user's call on
      // 2026-10-08, because few practitioners use that lab and a bullet costs
      // every other reader attention it does not earn. The lab is therefore
      // never named in a release note, which is a different thing from never
      // having shipped. The September block draws that same distinction about
      // Clinical Labs of Hawaii and Health Gorilla. If this ever needs
      // announcing, write it for the release that carries it and say that only
      // the most recent draw is read: these prints show several past draws
      // beside each result.
      //
      // STILL THE CALL as of 2026-10-10. A bullet was written for the October
      // 10 block above and withdrawn the same day, so this reasoning is the
      // live one and the lab has still never been named in a release note.
      //
      // THE SAME CALL, THE SAME DAY, FOR THE FUNCTION HEALTH APP EXPORT
      // (parsers/functionHealth.js, also live in prod with uploadPdf on
      // 2026-10-08): the PDF a member prints out of the Function Health app now
      // reads on its own. Left unannounced on 2026-10-08 for the CHI Health
      // reason, that few practitioners are handed one. Do not confuse it with
      // the Function Health reports that have read for months: those are
      // Quest-performed and go through Quest's rules, which is why no bullet
      // here has ever needed to name Function Health at all. Announce it with
      // whatever batch next touches the uploads, and say which of the two it
      // means.
      {
        group: 'Report tabs',
        /*
         * WRITTEN AS THE RENAME, old name first. A bullet headed "Results" and
         * describing the tab would read as a new feature, and send a
         * practitioner looking for a tab they have had all along; naming the
         * old label is what lets them match it to the strip in front of them.
         *
         * No em dash inside the sentence and no "your" in front of the
         * results: house style on both.
         *
         * SHORTENED on 2026-10-08, at the user's instruction, with the other
         * two bullets in this block. "of the analysis report" went because the
         * reader is being told about a tab they are about to open, and the
         * two-clause explanation of what the ranges are became one clause that
         * says the same thing by contrast ("for the results now, not for the
         * ranges"). The last sentence stays: it is the whole reassurance, and
         * it is the question a rename actually raises.
         *
         * NOT CLAIMED HERE: that anything moved, or that any content changed.
         * It is the same panel, the same bar graphs, the same order, in the
         * same position. Only the label on the tab is different, and the
         * second half of the desc says so in those words so nobody goes
         * hunting for what else was touched.
         */
        name: 'The "Reference Range" tab is now called "Results"',
        desc:
          'the first tab in every section, the one with the bar graphs. It is named for the results now, not for the ranges they are measured against. Nothing moved and nothing was removed',
      },
      {
        group: 'Report sections',
        /*
         * A SECOND RENAME IN THIS BLOCK RATHER THAN A BLOCK OF ITS OWN, and the
         * reason is the exact inverse of the trap the September 26 block
         * documents. This block has never reached prod: prod is still carrying
         * the October 5 note from 2026-10-06, so no practitioner has seen this
         * one and a bullet added to it is a bullet everyone is shown. (It did go
         * to slava in 0e37690 on 2026-10-08, which is why the id moved to -08 —
         * see its note above.) Check that again before adding a third bullet:
         * once this block reaches PROD, the next rename needs its own.
         *
         * GROUPED APART from the tab rename above because the scope differs.
         * That one renames the first tab in all 18 sections; this one renames a
         * single section. A practitioner scanning the note has to be able to
         * tell which of the two touched the thing they went looking for.
         *
         * Old name first, no em dash inside the sentence, no "your" in front of
         * the markers: the same three house rules as the bullet above.
         *
         * THE CLINICAL ARGUMENT IS DELIBERATELY NOT HERE. That CoQ10 is a
         * vitamin-like compound rather than a vitamin, and that
         * "Micronutrients" would have inherited the same problem, is why the
         * name changed, but it is a clinician's argument and not a release note.
         * One clause on the markers that already did not fit carries it.
         *
         * CoQ10 WAS NAMED HERE as something filed from My Markers, never as a
         * marker LabSmarts now charts, because it is not one, and a bullet
         * implying otherwise is the failure the October 5 block above guards
         * against. It came out in the 2026-10-08 shortening: it was the longest
         * clause in the bullet and the only forward-looking one, and the two
         * markers the section already carries make the same point about a name
         * being outgrown without needing the qualifier that kept it safe. If it
         * ever goes back, it goes back with that qualifier.
         *
         * ALSO CUT: "in the same order", which "in the same place" covers, and
         * the closing "Nothing moved and nothing was removed". That sentence is
         * carried by the tab bullet above, where it is the whole reassurance;
         * here the first clause already says it, and two adjacent bullets
         * ending in the same sentence read as a template rather than as news.
         */
        name: 'The "Vitamin & Mineral Status" section is now called "Nutrient Status"',
        desc:
          'the same markers in the same place on the report. Its Insights panel is now "Nutrient Insights" and the Results Overview pill reads "Nutrients". The old name had been outgrown by markers the section already carries, such as homocysteine and ceruloplasmin',
      },
      {
        group: 'PDF Lab Uploads',
        /*
         * "QUEST DIAGNOSTICS", THE FULL NAME, at the user's instruction on
         * 2026-10-08, and the two product names stay as they are: MyQuest and
         * Quest Quanum are what is printed on the reports themselves, so a
         * practitioner matches them by sight.
         *
         * SHORTENED on the same instruction. Two things went: the clause naming
         * the units, chart type and ranges a matched marker arrives with, which
         * is the import doing its job rather than news, and the sentence
         * crediting Labcorp with it since the last release, which was written
         * for a reader who had not seen that note and cost a line to serve.
         * What is left is the mechanism, then who does it.
         *
         * THE LIST IS EXACTLY THE PARSERS THAT RETURN `extras`, checked one by
         * one rather than assumed, the way the October 5 list was:
         *   - quest.js owns the shared `toExtras` and returns it on its own
         *     parse path; myQuest.js and questQuanum.js both call that helper.
         *     MEASURED on the corpus: 68 leftovers on Quest/18, 6 on MyQuest/15
         *     and 17 on the Quest-sourced Health Gorilla export. Quest Quanum
         *     returns ZERO on the one report on disk, not because the path is
         *     missing but because every row on that report mapped. It is named
         *     here as a capability, which is the same footing Labcorp has had
         *     since October 5 on a report with nothing left over, and the
         *     mechanism is generic: `claimTestIds` hands `toExtras` whatever it
         *     could not place. If a Quest Quanum report with unmapped rows ever
         *     reads as empty, this is the line that says it was never seen to
         *     work.
         *   - Health Gorilla gained a `/^Quest\b/i` entry in EXTRAS_BY_SOURCE.
         *     Named "Quest via Health Gorilla", matching the October 5 bullet's
         *     "Labcorp via Health Gorilla" word for word, and for that block's
         *     reason: Health Gorilla is an aggregator, and an unqualified
         *     mention promises every source lab. CPL-sourced exports return
         *     none.
         *
         * NOT NAMED: "all three Quest layouts". Three layouts is how this
         * repository thinks about Quest, not how a practitioner does; they have
         * a Quest report, and the bullet has to be true whichever one of the
         * three they are holding without asking them to know which.
         */
        name: 'A Quest Diagnostics upload fills in your own markers too',
        desc:
          'values the report prints that LabSmarts has no field for are matched to My Markers and added to the test, and anything left unplaced is named on screen so you can add it. MyQuest, Quest Quanum and Quest via Health Gorilla all do it',
      },
      {
        group: 'Reference ranges',
        /*
         * A DATA change, not a feature, and the only bullet here that is about
         * something which moved underneath practitioners rather than something
         * added. The ferritin rows in BloodworkRange-prod were replaced on
         * 2026-10-08 and the website bibliography updated in the same pass.
         *
         * It earns a bullet because nothing on screen announces it: no new
         * control appears, but a client's ferritin bar can sit in a different
         * band than it did last week, and the first a practitioner would know
         * of it is a client asking.
         *
         * THE DIRECTION IS THE WHOLE REASSURANCE, so the desc leads with it.
         * Not one upper limit moved and every lower limit rose, which means
         * results can only travel toward low and never toward high. A
         * practitioner who knows that does not have to re-read a report
         * wondering what else might have flipped.
         *
         * THE POPULATIONS ARE NAMED because "ferritin" alone would have them
         * checking adult results and missing that children and each trimester
         * moved too, and because women before and after menopause are now two
         * rows where there was one: a population that did not exist before.
         *
         * No "your" in front of results and no em dash inside a sentence,
         * as the bullets above.
         */
        name: 'Updated ferritin ranges',
        desc:
          'the lower limits have risen, for men, for women before and after menopause, for each trimester of pregnancy, and for children. Not one upper limit changed, so results move in one direction only: a ferritin that read in range may now read low',
        link: {
          href: 'https://labsmarts.com/bibliography/#ferritin',
          text: 'The studies behind each number \u2192',
        },
      },
    ],
    // Nothing on the Report Settings page in this release.
    settings: [],
  },
  {
    // Dated the DAY it ships rather than 'October 2026'. This block has waited
    // on the prod release since 2026-09-30 and been re-dated for it; the date a
    // practitioner reads has to be the day the note reached them, and a month
    // heading quietly stops being true the longer a release slips. Move it again
    // if prod goes out after today.
    date: 'October 5, 2026',
    features: [
      // WHAT IS DELIBERATELY NOT HERE, every one of them for the reason the
      // September 26 block below already documents — a bullet naming something
      // prod does not carry reads as a fault rather than a feature:
      //   - MY RANGES, when this block was written: finished and reviewable on
      //     slava, but myRangesVisible() closed again on 2026-10-01 with the
      //     release decision untaken. ANNOUNCED IN THE OCTOBER 13 BLOCK ABOVE,
      //     rewritten for ferritin alone rather than the five markers the
      //     bullets in 4872222 listed.
      //     (LEPTIN was on this list and is now announced below, at the user's
      //     instruction on 2026-10-05. Read the hard prerequisite on that group
      //     before this release goes to prod.)
      //   - MVX Plus, and the three calculated Liver / Biliary markers. Both
      //     still gated, both already noted in the blocks below.
      //   - The Virtual Clinic Practitioner ID and Client ID buttons, gated
      //     with the integration they belong to.
      {
        group: 'PDF Lab Uploads',
        /*
         * THE HEADLINE OF THIS RELEASE, and first for that reason. The
         * September 26 block deliberately said nothing about the rows a report
         * prints that LabSmarts has no field for, because back then only the
         * Hospital Joya panels produced any. Labcorp started producing them in
         * the 2026-10-01 deploy, and Labcorp is the lab a practitioner reading
         * this actually holds, so the reason for the silence is gone.
         *
         * Both halves have to be live for the bullet to be true, and they ship
         * by different routes: the placement is frontend (fillRowsFromImport,
         * via onExtraMarkers in bloodwork-entry) and has been in prod since
         * 2026-09-28, while the leftovers themselves come from the uploadPdf
         * Lambda, which the frontend deploy does not carry.
         *
         * Not caveated with the entitlement: canUseCustomMarkers() gates it, as
         * it gates every other My Markers bullet already published, and a note
         * that qualified each one would read as a price list.
         *
         * THE LAB LIST IS EXACTLY THE PARSERS THAT RETURN `extras`, and it is
         * not the same list as the parsers that read a report. Checked one by
         * one rather than assumed:
         *   - labcorp.js, cpl.js and hospitalJoya.js each build extras on their
         *     one parse path, so those uploads always offer them.
         *   - East Side is named because parsers/index.js routes 'EastSide' to
         *     parseCpl, so it offers them by sharing CPL's reader.
         *   - HEALTH GORILLA IS NAMED AS "LABCORP VIA HEALTH GORILLA", at the
         *     user's instruction on 2026-10-06, and the qualifier is the whole
         *     point of naming it. Health Gorilla is an aggregator: it reprints
         *     another lab's report in its own template, and its extras went
         *     through EXTRAS_BY_SOURCE, which held Labcorp and nothing else.
         *     Measured over the eight Health Gorilla reports on disk, exactly
         *     one offered leftovers, and it was the Labcorp-sourced one; the
         *     five Quest-sourced reports and the CPL-sourced one returned none.
         *     It was left unnamed before on the grounds that naming the
         *     aggregator would promise it for every source lab, and the
         *     phrasing is what answers that: a practitioner who uploads Health
         *     Gorilla PDFs can now tell that it works, and for which of them.
         *
         *     PAST TENSE SINCE 2026-10-08, and the bullet is now an
         *     UNDERSTATEMENT rather than a risk. uploadPdf shipped to prod that
         *     day with a `/^Quest\b/i` entry in EXTRAS_BY_SOURCE, so a
         *     Quest-sourced export offers leftovers too and the five reports
         *     above are no longer silent. The bullet is left exactly as prod
         *     read it: this block is dated, its id is long dismissed, and
         *     widening a shipped sentence rewrites what a practitioner was
         *     told. Announce the Quest half in the block that carries it.
         *   - Optimal DX and Clinical Labs of Hawaii return no extras at all,
         *     so the bullet below about them reading on their own is the whole
         *     of what they do. QUEST QUANUM WAS IN THIS LIST and came out on
         *     2026-10-08: all three Quest layouts (quest.js, myQuest.js,
         *     questQuanum.js) now return extras from the shared toExtras in
         *     quest.js.
         */
        // Hospital Joya carries a parenthetical where the other three do not: it
        // is the one lab here a practitioner may hold a report from without
        // recognizing the name, since the panels arrive through Hope4Cancer
        // rather than from a lab they chose. Bare name only, no claim about
        // whose lab it is; a clinic's arrangements change and a release note is
        // permanent. Cancun is dropped, the September 26 note having said it
        // once already, and Hope4Cancer is the cue that actually lands.
        name: 'A Labcorp upload fills in your own markers too',
        desc:
          'values the report prints that LabSmarts has no field for are matched to My Markers and added to the test with their units, chart type and ranges, and anything left unplaced is named on screen so you can add it. Labcorp, Labcorp via Health Gorilla, Clinical Pathology Laboratories, East Side Clinical Laboratory and Hospital Joya (Hope4Cancer) all offer them',
      },
      /*
       * NO "more labs read automatically" BULLET THIS TIME, and Clinical Labs of
       * Hawaii and Health Gorilla in particular are NOT new here. Both have been
       * live in prod for weeks, and the git log cannot show it because these
       * parsers ride the uploadPdf Lambda rather than the frontend deploy. The
       * function's own version history is the record:
       *   - healthGorilla.js was added 2026-09-01 and prod's uploadPdf v23 went
       *     out 2026-09-13.
       *   - clinicalLabsHawaii.js was added 2026-09-15 and prod v24 through v28
       *     went out that same day.
       * Neither was ever NAMED in a release note, which is a different thing
       * from being new, and a bullet announcing a lab a practitioner has been
       * uploading since September reads as though nobody here is keeping count.
       * The one upload change that genuinely reaches prod with this release is
       * the leftovers bullet above.
       */
      /*
       * PREREQUISITE, AND IT IS NOT A CODE CHANGE: BloodworkRange-prod must hold
       * leptin's 134 rows before this release reaches prod. The marker has NO
       * visibility flag, unlike the calculated Liver markers, so the field
       * appears the moment the frontend ships and a value typed into it can
       * never grade without the rows. The 134 are live in BloodworkRange-slava
       * (uploaded 2026-09-30, verified against findRange), and promoting them is
       * a prod DB write, so it is the user's step rather than something a deploy
       * carries.
       *
       * The pattern work is NOT a prerequisite and these bullets do not wait on
       * it. Leptin reaches the Glucose patterns through nothing: it is absent
       * from glucose-patterns.json, from glucose-pattern-insights.generated.ts
       * and from the categoryId resolution. Its clinical output is the two
       * possible root causes (6096 high, 6098 low) in GlucoseAnalysis.ts, which
       * fire off the marker's own range.
       */
      {
        group: 'New marker: Leptin',
        name: 'Leptin, in the Glucose section',
        desc:
          'entered under Additional Markers and charted with the Glucose panel on the analysis and comparison reports and in both PDFs',
      },
      {
        group: 'New marker: Leptin',
        /*
         * The COUNT is the point of this bullet, at the user's request, and 131
         * is the number of rows actually keyed to a BMI: 27 male and 26 female
         * adult rows for each whole BMI point, plus 26 more for each of the
         * three trimesters. Counted off the upload file rather than quoted, and
         * it is NOT the 134 rows leptin has in total: the other three are Quest
         * pediatric bands for ages 5 to 9, 10 to 13 and 14 to 17, which are
         * keyed to age and not to BMI. So the bullet claims BMI ranges only, and
         * children are left to the height and weight bullet below.
         */
        name: '131 ranges that change with BMI',
        desc:
          'leptin tracks body fat more closely than any other marker does, so Labcorp publishes a separate range for every whole BMI point and for each sex, with its own set for each trimester of a pregnancy. LabSmarts grades each client against the one that fits them',
      },
      {
        group: 'New marker: Leptin',
        // Unconditional in validate(), every age included, so this is safe to
        // state flatly.
        name: 'A leptin result needs the client’s height and weight',
        desc:
          'without them there is no BMI and so no range to grade against, and a test carrying a leptin value will not save until both are set, either on the client record or on the test itself',
      },
      {
        group: 'My Markers',
        name: 'Search the marker library',
        desc:
          'filter My Markers by name or section, with a count of how many markers match',
      },
      {
        group: 'My Markers',
        // Plain words for a mechanism, after the first draft packed the rule,
        // the reason and the badge into one sentence. Two short sentences are
        // the exception to the one-fragment house style here: the badge is a
        // separate fact, and a reader who stops after the first sentence has
        // still got the point.
        name: 'Repeat tests use your latest marker settings',
        desc:
          'a marker carried forward from the last test arrives with the units, chart type and ranges it has in My Markers today, not the ones that test used. A row that changed says Updated from My Markers',
      },
      {
        group: 'Raw data export for AI',
        name: 'The export downloads as a .txt file instead of .json',
        desc:
          'the contents are exactly the same, so the AI tools that refuse a .json upload accept the file as it downloads, with nothing to rename',
      },
    ],
    // Nothing new on the Report Settings page in this release. My Markers is a
    // page under Test Results, not a Report Settings header.
    settings: [],
  },
  {
    // A DAY, not a month: the release below went out earlier in September, and
    // two blocks headed "September 2026" would read as one note split in half.
    // Month headings stay the norm; a day is what a second release inside one
    // month needs to tell them apart.
    date: 'September 26, 2026',
    features: [
      // NO LIVER / BILIARY GROUP HERE. The three calculated markers — AST/ALT
      // Ratio, Hepatic Steatosis Index and FIB-4 — were written into this
      // release and taken back out on 2026-09-27: they are held behind
      // liverCalculatedMarkersVisible() until their ranges pass review, and
      // this block is what a practitioner reads the first time prod carries
      // it. Same reason the Nutrient Status group below announces no pattern
      // boxes. They do NOT come back here when the hold lifts: this entry is
      // dated, and its ANNOUNCEMENT_ID has already been marked seen, so a
      // bullet appearing under it later is a bullet nobody is shown. Announce
      // them in whatever release actually carries them to prod.
      //
      // The labs are named because recognizing one's own lab is the whole
      // point of the bullet. Nothing here about the rows a report prints that
      // LabSmarts has no field for: that only fires on the Hospital Joya
      // panels today, so it waits until those reports are something a
      // practitioner reading this would have in hand.
      {
        group: 'PDF Lab Uploads - 4 more report types',
        name: 'More labs read automatically',
        desc:
          'Optimal DX, Quest Quanum, Hospital Joya in Cancun and a third Clinical Pathology Laboratories format parse on their own',
      },
      {
        group: 'Test Results Comparison Tables',
        // Nothing about where a note STARTS: the summary LabSmarts used to
        // write into the column was taken back out, so the column is the
        // practitioner's own text and nothing else.
        name: 'Notes column on the Test Results Comparison tables',
        desc:
          'one note per section, yours to type, saved with the client and printed on the with-notes PDF',
      },
      {
        group: 'Test Results Comparison Tables',
        name: 'Hide Lab and Alarm range columns from the comparison tables',
        desc:
          'checkboxes above each table drop that pair from the report you are on, and the headers that stay carry a band of color for the zone they name',
      },
      {
        group: 'More PDF report customizations',
        // The panel never leaves the SCREEN, which is the half a practitioner
        // has to be told or the checkbox reads as "hide the Insights panel".
        name: 'Keep an Insights Panel off the PDF',
        desc:
          'a checkbox beside each section Include in PDF prints the bar graphs without the panel above them, and the panel stays on screen either way',
      },
      {
        group: 'Enhanced Supplement Dosing',
        // Named as Systemic Formulas' own two words, which is how the guide
        // and the radio on the panel both put it.
        name: 'Maintenance and therapeutic doses',
        desc:
          'Systemic Formulas servings straight from the Practitioner Clinical Guide, with one choice switching the client list between them',
      },
      {
        group: 'Female Client Enhancements',
        name: 'Premenopause explained where you choose it',
        desc:
          'an info icon on Menopause/Pregnant covers infants, children and teens, and one beside Menstrual Phase opens a cycle phase diagram',
      },
      {
        group: 'Female Client Enhancements',
        // Framed as what the option DID, not as a field being taken away.
        name: 'Trimester no longer asks for "Unknown"',
        desc: 'those ranges were always the first trimester ranges',
      },
    ],
    settings: [
      // Each of these names what the SETTING adds over the feature bullet above
      // it, which is the default it sets. Restating the feature here is how the
      // first draft read, and it made the whole block look like a second copy
      // of the list.
      {
        name: 'Comparison Table Notes',
        desc: 'turn the Notes column off on screen, in the PDF, or both, and choose which note blocks print after the tables',
      },
      {
        name: 'Comparison Table Columns',
        desc: 'where the Hide Alarm and Hide Lab checkboxes start on every report',
      },
      {
        name: 'Insights Panels to include',
        desc: 'which sections print their Insights Panel by default',
      },
    ],
  },
  {
    date: 'September 2026',
    features: [
      // HIGH-LEVEL FEATURES ONLY. This is a release note, not a changelog:
      // fixes and refinements (a note's arrows rendering properly, a cross-link
      // inside one marker's root causes) belong in the work, not here. A bullet
      // earns its place by being something a practitioner would go and try.
      // The group heading carries the framing on its own: "Add any marker, from
      // any test" already says the markers need not be blood work, and every
      // bullet under it is something to go and do. A "More than blood work"
      // headline bullet sat above them making the same point a second time in
      // the register of a marketing line, which is where that claim belongs.
      {
        group: 'Add any marker, from any test',
        name: 'Add your own markers to a test',
        desc:
          'in Add Test Results, for anything your lab measured that LabSmarts does not already carry',
      },
      {
        group: 'Add any marker, from any test',
        // The bullet above reads as "for the odd marker LabSmarts is missing",
        // and a practitioner who stops there never finds out that the marker
        // can go INTO a section rather than into an appendix at the end.
        // The two section names are examples introduced by "such as" and the
        // clause about sections you create follows them, so the pair reads as
        // an illustration of "any section" rather than as the only two on
        // offer. Naming none of them was the alternative, and an abstraction on
        // its own does not land in one read.
        name: 'File them into any section',
        desc:
          'a marker of your own can sit in a LabSmarts section, such as Hormone Balance or Kidney Function, or in any section that you create, charted against the ranges you set',
      },
      {
        group: 'Add any marker, from any test',
        // Numbers are what "marker" means everywhere else in LabSmarts, so a
        // urinalysis or a culture reads as out of scope until this says
        // otherwise. The expected wording is half the feature and belongs in
        // the same bullet: without it a text row is just a printed word.
        name: 'Results reported in words',
        desc:
          'a urinalysis "Negative" or a "Trace" prints on the report, and setting the expected wording makes anything else stand out',
      },
      {
        group: 'Add any marker, from any test',
        // Two examples that are nothing like each other, so the pair reads as
        // "anything" rather than "antibody panels": an IgG allergen panel and a
        // mycotoxin panel sat too close together to do that work. The point of
        // the bullet is the pairing with the one above it, so it ends on the
        // markers going into the section rather than on the naming.
        name: 'Add your own sections',
        desc:
          'a viral antigen panel, a mycotoxin panel: name the section yourself and file your own markers into it',
      },
      {
        // Named for both lists the page holds, since My Sections is half of
        // what is new here. The desc still points at the "My Markers" menu
        // item, which is what a practitioner will be looking for.
        group: 'Add any marker, from any test',
        name: 'My Markers & My Sections',
        desc:
          'your definitions saved once and reused on every test, on the My Markers page under the Test Results menu',
      },
      {
        group: 'Add any marker, from any test',
        // "Reused on every test" in the bullet above still means picking them
        // each time. This is the step past that, and it is the one that saves a
        // practitioner running the same panel on every client any typing at
        // all — so it earns a bullet rather than a clause.
        name: 'Add to every new test',
        desc:
          'tick a whole section or a single marker in My Markers and it is waiting on every test you start from then on',
      },
      // NOT listed here: "no limit on markers". Custom markers arrive in this
      // very release, so no practitioner has ever met a cap on them — announcing
      // the absence of one plants the idea that a limit was on the cards. Same
      // reason there is no bullet about a limit on sections.
      {
        group: 'Supplement protocols',
        name: 'Systemic Formulas protocols',
        desc: 'supplement recommendations matched to the patterns each section detects',
      },
      {
        group: 'Supplement protocols',
        name: 'Three views of the protocol',
        desc:
          'Top Priority, By System, or the full Roadmap, each with its own Include in PDF setting',
      },
      {
        group: 'Supplement protocols',
        // Dosing prints in exactly one place, and a practitioner who does not
        // know that goes looking for it on the section tabs.
        name: 'A client list with dosing',
        desc:
          'tick the products you want and the report prints a client-facing section with names, descriptions and dosing',
      },
      // NO GAUGE, NO PATTERN BOXES AND NO "NOT MEASURED YET" IN THIS GROUP.
      // All three were announced here once and none ships: the gauge was ruled
      // out by design (the marker IS the finding, so a likelihood word adds
      // nothing to "Vitamin D 22 ng/mL, Priority"), and the one-carbon pattern
      // boxes and the open questions are both held behind
      // nutrientPatternsVisible() until the "Nutrient Patterns" tab passes
      // clinical review. What the panel actually renders is the status tally
      // and the Focus list, so that is what these bullets describe.
      // The group heading matches the panel's own header, "Nutrient
      // Insights", rather than the section heading "Nutrient Status". Both were
      // renamed on 2026-10-07 (from "Vitamin & Mineral Insights" and "Vitamin &
      // Mineral Status"); this dated entry was updated with them so the group
      // heading still names a panel a practitioner can find.
      {
        group: 'Nutrient Insights',
        name: 'A new Insights panel',
        desc:
          'the section now opens with a count of its Priority, Support, Monitor and Optimal markers',
      },
      {
        group: 'Nutrient Insights',
        name: 'A Focus list, worst first',
        desc:
          'every marker below optimal, grouped under its status with an arrow for the direction, on screen and in the client PDF',
      },
      {
        group: 'Stress & Adrenal Insights',
        // Was "the same panel shape", which pointed at the Nutrient Insights
        // bullets above. Those describe a panel with neither a gauge nor
        // pattern boxes, so the comparison now names Glucose directly.
        name: 'A likelihood gauge, then the patterns',
        desc:
          'the section opens the way Glucose does: the gauge names the condition and how likely it is, then a box for each pattern the adrenal markers point to',
      },
      {
        group: 'Stress & Adrenal Insights',
        // CORTISOL LEADS, unconditionally. Read adrenalGauge in
        // StressAdrenalAnalysis.ts, not the panel's info popover: the
        // 2026-08-23 change made cortisol's direction win outright
        // (`cortisolDir !== null ? cortisolDir : nakDir`), including when it
        // disagrees with a deviating Na/K, because reporting underactivity off
        // the ratio while the pattern box below showed a high cortisol was a
        // visible self-contradiction. Na/K is a weak aldosterone proxy that
        // also moves for non-adrenal reasons, so it only fills in when cortisol
        // is silent and is capped at 68% when it is the only deviating marker.
        // An earlier version of this bullet had the two the other way round.
        name: 'Cortisol sets the direction',
        desc:
          'the sodium/potassium ratio fills in when cortisol reads optimal or was not ordered, and on its own the ratio reports as supporting evidence rather than a verdict',
      },
      {
        group: 'Stress & Adrenal Insights',
        // "Reduced Stress Resilience" is the gauge's own label for the
        // 'reserve' condition, so the bullet uses those words exactly.
        name: 'DHEA-S and pregnenolone read the reserve',
        desc:
          'with cortisol and the ratio both optimal, a low reserve reads as reduced stress resilience, which shows before function itself slips',
      },
      {
        group: 'On the report',
        // NOT "Insights carry over": they only rebuild from the previous
        // collection when the section has no current results at all. A section
        // that WAS rerun keeps its own current Insights, so a bullet promising
        // carried-over Insights would be wrong most of the time. Framed as what
        // the practitioner can rely on instead: nothing drops off the report.
        name: 'Nothing disappears between tests',
        desc:
          'a panel the client did not repeat still shows its last results and findings, labeled with the date they were collected',
      },
      {
        group: 'On the report',
        // Bolded whole: this is the Results Overview's own branded name, so it
        // reads as one thing rather than a heading with a suffix.
        name: 'The Functional Blood Work Map™',
        desc:
          'the Results Overview now carries its own name, plus a marker count on every section',
      },
      {
        group: 'Around the app',
        name: 'Menus reorganized',
        desc:
          'Report Settings and My Markers moved to the Test Results menu, with "Prepared by" and your logo; Account now holds your Profile and subscription',
      },
      {
        group: 'Around the app',
        name: 'Redesigned login screens',
        desc: 'sign-in, sign-up and password',
      },
      {
        group: 'Around the app',
        name: 'Book a Clinical Review',
        desc: 'from the top menu',
      },
      {
        group: 'Around the app',
        name: 'Automatic sign-out after an hour',
        desc:
          'a warning appears five minutes ahead with a Stay signed in button, so client information is not left on an unattended screen; save your work when you see it',
      },
    ],
    // Name the OPTION that is new, not the setting group it sits under. Both
    // groups here shipped in June 2026, so listing a group by name reads as
    // "this setting is new" when it isn't:
    //   - PDF Inclusion is left out entirely. Every section on the report
    //     carries an "Include in PDF" checkbox by design, practitioner-named
    //     ones included, so a new section appearing in that list is the design
    //     working rather than a new setting.
    //   - Markers Outside Optimal is listed by its new "Minimize section"
    //     choice, alongside the show/collapse/hide choices it already had.
    settings: [
      {
        name: 'Report Titles',
        desc: 'the heading on each PDF cover page is yours to set, and no longer names blood work by default',
      },
      {
        name: 'Minimize Markers Outside Optimal section',
        desc: 'a new choice that collapses the section to its header on screen while still exporting it in full',
      },
      // Named as the whole group, which the note above warns against for
      // settings that already shipped. This one is the exception: supplements
      // were never visible in prod, so every option under this header is new
      // to the practitioners reading this.
      {
        name: 'Supplement Settings',
        desc: 'choose which supplement brands your reports draw from',
      },
    ],
  },
  {
    date: 'July 2026',
    features: [
      'Results Overview redesign',
      'About tab in every section for marker info',
      'Notes tab redesign with a consolidated Section Notes summary',
      'Estimated whole-blood viscosity calculation',
      'Raw data export with an AI prompt',
    ],
    settings: [
      {
        name: 'Test Comparison Bar Graphs',
        desc: 'also chart markers measured in the previous test but not the current one',
      },
    ],
  },
  {
    date: 'June 2026',
    features: [
      'Results Overview',
      'Markers Outside Optimal',
      'Glucose Dysregulation gauge & patterns',
      'Stress & Adrenal Balance section',
      'Floating Save button when adding & editing tests',
      'BMI bar graph',
    ],
    settings: [
      {
        name: 'PDF Inclusion',
        desc: 'choose which sections appear in the exported PDF',
      },
      {
        name: 'Markers Outside Optimal',
        desc: 'show, collapse, or hide the reference-range bar charts',
      },
      {
        name: 'Inflammation Section',
        desc: 'show only markers that signal here, or all of them',
      },
      {
        name: 'Immune Section',
        desc: 'control which immune markers and differential graphs appear',
      },
      {
        name: 'Blood Work Comparison Section Order',
        desc: 'match the analysis page order or the classic lab-report order',
      },
    ],
    blogUrl: 'https://labsmarts.com/new-labsmarts-features-june-2026/',
  },
];

// The nav label while this release is still unseen: "New in August" rather than
// the standing "What's New", because the standing label looks the same whether
// or not there is anything behind it and practitioners were reading past it.
// Derived from the newest release rather than typed, so it can never announce a
// month the changelog above has moved on from.
const unseenNavLabel = (): string => {
  const month = RELEASES[0]?.date.split(' ')[0];
  return month ? `New in ${month}` : ANNOUNCEMENT_TITLE;
};

interface OwnProps {
  // Namespaces the "seen" flag per practitioner so each account dismisses the
  // note independently (matching the existing per-profile localStorage pattern).
  userProfileId?: string;
}

type Props = OwnProps;

type State = {
  showNote: boolean;
  dismissed: boolean;
};

class SettingsAnnouncementBase extends React.Component<Props, State> {
  // Anchor for the controlled Overlay so the popover positions under the badge.
  private targetRef = React.createRef<HTMLDivElement>();

  // The auto-open fires at most once per mount. Without this, closing the
  // popover by clicking away (rootClose, which deliberately does NOT dismiss)
  // would leave `dismissed` false, and the next re-render would open it again —
  // a note the practitioner just closed would keep reappearing under them.
  private autoOpened = false;

  state: State = {
    showNote: false,
    dismissed: this.readDismissed(),
  };

  private storageKey(): string {
    return `${this.props.userProfileId}-announcement-${ANNOUNCEMENT_ID}`;
  }

  private readDismissed(): boolean {
    // Until the profile loads we don't know the user, so suppress the badge to
    // avoid flashing it for someone who already dismissed it. componentDidUpdate
    // re-reads the real value once userProfileId arrives.
    if (!this.props.userProfileId) {
      return true;
    }
    return localStorage.getItem(this.storageKey()) === 'true';
  }

  componentDidMount() {
    this.maybeAutoOpen();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.userProfileId !== this.props.userProfileId) {
      this.setState({ dismissed: this.readDismissed() });
    }
    this.maybeAutoOpen();
  }

  /**
   * Open the note by itself the first time a practitioner loads the app after a
   * new release, so a changelog nobody clicks still reaches them. "Got it" is
   * what stops it: it writes ANNOUNCEMENT_ID to localStorage, so the next bump
   * is the only thing that brings it back.
   *
   * Runs from didMount/didUpdate rather than the state initialiser because the
   * Overlay positions against `targetRef.current`, which is null during the
   * render that creates the anchor. Setting state here schedules one more
   * render, by which point the ref is attached and the popover lands under the
   * badge instead of in the corner of the page. The same beat also waits out
   * `userProfileId` arriving — until it does, render() returns null and
   * readDismissed() reports "dismissed" so the badge never flashes at someone
   * who already closed it.
   */
  private maybeAutoOpen(): void {
    if (this.autoOpened) return;
    if (!this.props.userProfileId) return;
    if (this.readDismissed()) return;

    this.autoOpened = true;
    this.setState({ showNote: true });
  }

  // Open/close the note without dismissing it — lets a user re-read it later.
  toggleNote = () => {
    this.setState(prev => ({ showNote: !prev.showNote }));
  };

  closeNote = () => {
    this.setState({ showNote: false });
  };

  // Permanently clears the "New" badge for this user (persists across reloads).
  dismiss = () => {
    if (this.props.userProfileId) {
      localStorage.setItem(this.storageKey(), 'true');
    }
    this.setState({ dismissed: true, showNote: false });
  };

  render() {
    // Wait for the profile so the dismissed state is keyed to the right user.
    if (!this.props.userProfileId) {
      return null;
    }

    const { showNote, dismissed } = this.state;

    return (
      <Nav.Item>
        <div
          ref={this.targetRef}
          className="nav-link"
          role="button"
          onClick={this.toggleNote}
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            color: '#fff',
          }}
        >
          <FontAwesomeIcon icon="bell" className="ml-2 mr-2" />
          {/* Reverts to the standing label once dismissed, so the nav doesn't
              keep advertising a month the practitioner has already read. */}
          <span>{dismissed ? ANNOUNCEMENT_TITLE : unseenNavLabel()}</span>
          {!dismissed && (
            <Badge variant="danger" pill className="ml-1">
              New
            </Badge>
          )}
        </div>

        <Overlay
          target={this.targetRef.current}
          show={showNote}
          placement="bottom"
          rootClose
          onHide={this.closeNote}
        >
          <Popover
            id="settings-announcement-popover"
            className="whats-new-popover"
          >
            {/* Dismiss sits in the title row, not under the changelog: the note
                opens itself now, so the way to close it has to be visible the
                moment it appears rather than after a scroll through every
                release. Labeled rather than an "×" because this is permanent —
                it marks the release read — and an × reads as "close for now",
                which is what clicking away already does. */}
            <Popover.Title as="h3" className="whats-new-title">
              <span>{ANNOUNCEMENT_TITLE}</span>
              <Button
                variant="primary"
                size="sm"
                className="whats-new-dismiss"
                onClick={this.dismiss}
              >
                Got it
              </Button>
            </Popover.Title>
            <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p className="mb-2">{ANNOUNCEMENT_INTRO}</p>

              {RELEASES.map(release => (
                <div key={release.date} className="whats-new-release mb-3">
                  <div className="whats-new-release-date">
                    Release date: {release.date}
                  </div>

                  {release.features.length > 0 && (
                    <>
                      <div className="font-weight-bold mb-1">New features</div>
                      {groupFeatures(release.features).map((run, runIndex) => (
                        <React.Fragment key={run.group || `ungrouped-${runIndex}`}>
                          {run.group && (
                            <div className="whats-new-group">{run.group}</div>
                          )}
                          <ul className="whats-new-list mb-2">
                            {run.items.map(feature =>
                              typeof feature === 'string' ? (
                                <li key={feature}>{feature}</li>
                              ) : (
                                <li key={feature.name}>
                                  <strong>{feature.name}</strong>
                                  {feature.desc && <> &mdash; {feature.desc}</>}
                                  {feature.link && (
                                    <>
                                      {' '}
                                      <a
                                        href={feature.link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {feature.link.text}
                                      </a>
                                    </>
                                  )}
                                </li>
                              ),
                            )}
                          </ul>
                        </React.Fragment>
                      ))}
                    </>
                  )}

                  {release.settings.length > 0 && (
                    <>
                      <div className="whats-new-settings-heading">New settings</div>
                      <ul className="whats-new-list mb-2">
                        {release.settings.map(setting => (
                          <li key={setting.name}>
                            <strong>{setting.name}</strong> &mdash; {setting.desc}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {release.blogUrl && (
                    <p className="whats-new-release-blog mb-0">
                      <a
                        href={release.blogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read more about the {release.date} release &rarr;
                      </a>
                    </p>
                  )}
                </div>
              ))}

            </Popover.Content>
          </Popover>
        </Overlay>
      </Nav.Item>
    );
  }
}

export const SettingsAnnouncement = SettingsAnnouncementBase;
